import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DashboardLayout from "../../../components/DashboardLayout";
import {
  getSeminarsAdminAPI,
  getSeminarStudentsAPI,
  assignSeminarStudentAPI,
  removeSeminarStudentAPI,
} from "../../../services/portal/admin/seminarService";
import { getStudentsAPI } from "../../../services/portal/admin/studentService";
import {
  showErrorToastMsg,
  showSuccessToastMsg,
} from "../../../utility/ToastMessage";
import Loader from "../../../components/Loader";
import RemoveConfirmationDialog from "../../../components/portal/RemoveConfirmationDialog";
import { formatDate, tableHeaderColor } from "../../../utility/Common";
import { RoutePaths } from "../../../utility/RoutePaths";

const ITEMS_PER_PAGE = 10;

const ManageSeminarStudents = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const seminarId = searchParams.get("seminarId");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [seminarDetails, setSeminarDetails] = useState(null);
  const [assignedStudents, setAssignedStudents] = useState([]);

  // All students table
  const [allStudents, setAllStudents] = useState([]);
  const [filteredAllStudents, setFilteredAllStudents] = useState([]);
  const [loadingAllStudents, setLoadingAllStudents] = useState(false);
  const [searchQueryAll, setSearchQueryAll] = useState("");
  const [currentPageAll, setCurrentPageAll] = useState(1);

  // Assign modal
  const [assignStudent, setAssignStudent] = useState(null);

  // Remove
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const loadSeminarDetails = async () => {
    try {
      const res = await getSeminarsAdminAPI();
      if (res && res.IsSuccessful && res.Data) {
        const item = res.Data.find((s) => String(s.Id) === String(seminarId));
        setSeminarDetails(item || null);
      }
    } catch (error) {
      console.error("Error loading seminar details:", error);
    }
  };

  const loadAssignedStudents = useCallback(async () => {
    if (!seminarId) return;
    setLoading(true);
    try {
      const res = await getSeminarStudentsAPI(seminarId);
      if (res && res.IsSuccessful && Array.isArray(res.Data)) {
        setAssignedStudents(res.Data);
      } else {
        setAssignedStudents([]);
      }
    } catch (error) {
      console.error("Error loading seminar students:", error);
      showErrorToastMsg("Failed to load assigned students");
      setAssignedStudents([]);
    } finally {
      setLoading(false);
    }
  }, [seminarId]);

  const loadAllStudents = async () => {
    setLoadingAllStudents(true);
    try {
      const res = await getStudentsAPI(0);
      if (res && res.IsSuccessful && res.Data) {
        setAllStudents(res.Data);
      } else {
        setAllStudents([]);
      }
    } catch (error) {
      console.error("Error loading all students:", error);
      showErrorToastMsg("Failed to load students");
      setAllStudents([]);
    } finally {
      setLoadingAllStudents(false);
    }
  };

  useEffect(() => {
    loadSeminarDetails();
    loadAllStudents();
  }, []);

  useEffect(() => {
    loadAssignedStudents();
  }, [loadAssignedStudents]);

  // Filter all students by search query
  useEffect(() => {
    const query = searchQueryAll.trim().toLowerCase();
    if (!query) {
      setFilteredAllStudents(allStudents);
    } else {
      setFilteredAllStudents(
        allStudents.filter((s) => {
          const name = `${s.FirstName || ""} ${s.LastName || ""}`.toLowerCase();
          const mobile = (s.MobileNo || "").toLowerCase();
          const email = (s.Email || "").toLowerCase();
          const studentId = (s.StudentId || "").toLowerCase();
          const city = (s.City || "").toLowerCase();
          return (
            name.includes(query) ||
            mobile.includes(query) ||
            email.includes(query) ||
            studentId.includes(query) ||
            city.includes(query)
          );
        }),
      );
    }
    setCurrentPageAll(1);
  }, [searchQueryAll, allStudents]);

  // Pagination
  const totalPagesAll =
    Math.ceil(filteredAllStudents.length / ITEMS_PER_PAGE) || 1;
  const startIndexAll = (currentPageAll - 1) * ITEMS_PER_PAGE;
  const paginatedAll = filteredAllStudents.slice(
    startIndexAll,
    startIndexAll + ITEMS_PER_PAGE,
  );

  const isAssigned = (student) =>
    assignedStudents.some((s) => String(s.UserId) === String(student.UserId));

  const handleAssign = async (student) => {
    if (isAssigned(student)) {
      showErrorToastMsg("Student is already assigned to this seminar");
      return;
    }
    setSaving(true);
    try {
      const res = await assignSeminarStudentAPI({
        seminarId: Number(seminarId),
        studentId: student.UserId,
        isFree: 0,
      });
      if (res && res.IsSuccessful) {
        showSuccessToastMsg("Student assigned successfully");
        setAssignStudent(null);
        await loadAssignedStudents();
      } else {
        showErrorToastMsg(res?.Message || "Failed to assign student");
      }
    } catch (error) {
      console.error("Error assigning student:", error);
      showErrorToastMsg("Failed to assign student");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = (item) => {
    setDeleteItem(item);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;
    setSaving(true);
    try {
      const res = await removeSeminarStudentAPI(Number(deleteItem.StudentSeminarAssignId));
      if (res && res.IsSuccessful) {
        showSuccessToastMsg("Student removed successfully");
        await loadAssignedStudents();
      } else {
        showErrorToastMsg(res?.Message || "Failed to remove student");
      }
    } catch (error) {
      console.error("Error removing student:", error);
      showErrorToastMsg("Failed to remove student");
    } finally {
      setSaving(false);
      setShowDeleteConfirm(false);
      setDeleteItem(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeleteItem(null);
  };

  const renderPagination = (currentPage, totalPages, setPage) => (
    <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex flex-wrap items-center justify-between gap-2">
      <div className="text-sm text-gray-600">
        Showing {startIndexAll + 1} to{" "}
        {Math.min(startIndexAll + ITEMS_PER_PAGE, filteredAllStudents.length)}{" "}
        of {filteredAllStudents.length} results
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={currentPage === 1}
          className="px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        {Array.from({ length: totalPages }, (_, i) => i + 1)
          .filter((p) => {
            if (totalPages <= 7) return true;
            if (p === 1 || p === totalPages) return true;
            if (Math.abs(p - currentPage) <= 1) return true;
            return false;
          })
          .map((p, idx, arr) => (
            <React.Fragment key={p}>
              {idx > 0 && arr[idx - 1] !== p - 1 && (
                <span className="px-2 text-gray-400">…</span>
              )}
              <button
                onClick={() => setPage(p)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md ${
                  currentPage === p
                    ? "bg-primary-600 text-white"
                    : "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                {p}
              </button>
            </React.Fragment>
          ))}
        <button
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          disabled={currentPage === totalPages}
          className="px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
    </div>
  );

  return (
    <DashboardLayout
      title="Seminar Students"
      subtitle="Manage students assigned to this seminar"
    >
      <div className="mb-4">
        <button
          type="button"
          onClick={() => navigate(RoutePaths.ADMIN_MANAGE_SEMINARS_PAGE)}
          className="text-sm font-medium text-primary-600 hover:text-primary-800"
        >
          ← Back to seminars
        </button>
      </div>

      {seminarDetails && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6 border-l-4 border-primary-600">
          <h2 className="text-xl font-bold text-gray-900">
            {seminarDetails.Title}
          </h2>
          {seminarDetails.Description && (
            <p className="text-sm text-gray-600 mt-1">
              {seminarDetails.Description}
            </p>
          )}
          <div className="flex gap-6 mt-2">
            {seminarDetails.Date && (
              <p className="text-sm text-gray-500">
                Date: {new Date(seminarDetails.Date).toLocaleDateString()}
              </p>
            )}
            {seminarDetails.Fee !== undefined &&
              seminarDetails.Fee !== null && (
                <p className="text-sm text-gray-500">
                  Fee: Rs. {seminarDetails.Fee.toLocaleString()}
                </p>
              )}
          </div>
        </div>
      )}

      <div className="space-y-8">
        {/* Assigned Students */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div
            className={`${tableHeaderColor} px-6 py-4 border-b border-gray-200`}
          >
            <h4 className="text-md font-medium text-white">
              Assigned Students ({assignedStudents.length})
            </h4>
          </div>
          <div className="overflow-x-auto">
            {loading ? (
              <div className="px-6 py-8 flex justify-center">
                <Loader />
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Access
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Mobile Number
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Student ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Address
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {assignedStudents.length === 0 ? (
                    <tr>
                      <td
                        colSpan={4}
                        className="px-6 py-8 text-center text-sm text-gray-500"
                      >
                        No students assigned yet.
                      </td>
                    </tr>
                  ) : (
                    assignedStudents.map((item) => (
                      <tr key={item.Id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {item.FirstName + " " + item.LastName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {item.PaymentStatus}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {item.MobileNo}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.Email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {item.StudentId}
                        </td>
                        <td className="px-6 py-4 whitespace-wrap text-sm text-gray-900">
                          {item.AddressLine1 +
                            ", " +
                            item.Street +
                            ", " +
                            item.City}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-right">
                          {item.PaymentStatus === "Assigned By Admin" && (
                            <button
                              type="button"
                              onClick={() => handleRemove(item)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Remove
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* All Students */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div
            className={`${tableHeaderColor} px-6 py-4 border-b border-gray-200`}
          >
            <h4 className="text-md font-medium text-white">
              All Students ({filteredAllStudents.length})
            </h4>
          </div>
          <div className="overflow-x-auto">
            <div className="p-4">
              <input
                type="text"
                placeholder="Search by name, email, mobile, student ID, city..."
                value={searchQueryAll}
                onChange={(e) => setSearchQueryAll(e.target.value)}
                className="w-full max-w-md text-sm px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Mobile Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Student ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    City
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Registered Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loadingAllStudents ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-6 py-8 text-center text-gray-500"
                    >
                      <Loader />
                    </td>
                  </tr>
                ) : paginatedAll.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-6 py-8 text-center text-sm text-gray-500"
                    >
                      No students found.
                    </td>
                  </tr>
                ) : (
                  paginatedAll.map((student) => (
                    <tr key={student.UserId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {student.FirstName} {student.LastName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {student.MobileNo}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {student.Email || (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {student.StudentId}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {student.City}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {student.CreatedAt ? formatDate(student.CreatedAt) : ""}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {isAssigned(student) ? (
                          <span className="text-gray-400">Assigned</span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setAssignStudent(student)}
                            className="text-primary-600 hover:text-primary-900 font-medium"
                          >
                            Assign
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {filteredAllStudents.length > ITEMS_PER_PAGE &&
            renderPagination(currentPageAll, totalPagesAll, setCurrentPageAll)}
        </div>
      </div>

      {showDeleteConfirm && (
        <RemoveConfirmationDialog
          loading={saving}
          description={
            deleteItem ? `${deleteItem.FirstName} ${deleteItem.LastName}` : ""
          }
          cancelDelete={cancelDelete}
          confirmDelete={confirmDelete}
        />
      )}

      {assignStudent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className={`${tableHeaderColor} px-6 py-4 rounded-t-lg`}>
              <h4 className="text-md font-medium text-white">Assign Student</h4>
            </div>
            <div className="p-6 space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Name</span>
                  <span className="text-sm font-medium text-gray-900">
                    {assignStudent.FirstName} {assignStudent.LastName}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Mobile</span>
                  <span className="text-sm font-medium text-gray-900">
                    {assignStudent.MobileNo}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Student ID</span>
                  <span className="text-sm font-medium text-gray-900">
                    {assignStudent.StudentId}
                  </span>
                </div>
                {assignStudent.Email && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Email</span>
                    <span className="text-sm font-medium text-gray-900">
                      {assignStudent.Email}
                    </span>
                  </div>
                )}
                {assignStudent.City && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">City</span>
                    <span className="text-sm font-medium text-gray-900">
                      {assignStudent.City}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex gap-3 justify-end pt-2">
                <button
                  type="button"
                  onClick={() => setAssignStudent(null)}
                  disabled={saving}
                  className="text-sm font-medium px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleAssign(assignStudent)}
                  disabled={saving}
                  className={`text-sm font-medium px-4 py-2 rounded-md transition-colors ${
                    saving
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-primary-600 text-white hover:bg-primary-700"
                  }`}
                >
                  {saving ? "Assigning..." : "Assign"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default ManageSeminarStudents;
